import { useCallback, useEffect, useState } from 'react';
import { UnauthenticatedError, api, pct, type PocProgress, type PocReport } from '../lib/api.ts';

/**
 * Phase 0 results screen — the feasibility gate.
 *
 * This page answers one question: how much of an arbitrage gap is still there
 * by the time a transferred coin would have landed? Everything on it is a
 * measurement, not a projection.
 *
 * The decay table is the deliverable. Two survival columns, because they mean
 * different things: "still profitable" asks whether the trade was worth doing,
 * "still breakeven" asks whether it lost money. A method where most gaps decay
 * through zero cannot be rescued by raising the entry bar, since the losers
 * come from the same price movement as the winners.
 */
export function Phase0() {
  const [progress, setProgress] = useState<PocProgress | null>(null);
  const [report, setReport] = useState<PocReport | null>(null);
  const [hours, setHours] = useState(168);
  const [threshold, setThreshold] = useState(1.5);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [p, r] = await Promise.all([api.pocProgress(), api.pocReport(hours, threshold)]);
      setProgress(p);
      setReport(r.report);
    } catch (err) {
      if (!(err instanceof UnauthenticatedError)) {
        setError(err instanceof Error ? err.message : 'failed to load Phase 0 data');
      }
    } finally {
      setLoading(false);
    }
  }, [hours, threshold]);

  useEffect(() => {
    void load();
  }, [load]);

  const verdictClass =
    report?.verdict.decision === 'go'
      ? 'ok'
      : report?.verdict.decision === 'no-go'
        ? 'bad'
        : 'warn';

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Phase 0 — gap persistence</h1>
          <div className="sub">
            Read-only measurement. No keys, no orders. Does an arbitrage gap survive the 2–30
            minutes a coin spends in transit?
          </div>
        </div>
        <div className="row">
          <div style={{ width: 130 }}>
            <label>Window (hours)</label>
            <input
              type="number"
              value={hours}
              min={1}
              onChange={(e) => setHours(Number(e.target.value) || 168)}
            />
          </div>
          <div style={{ width: 130 }}>
            <label>Threshold %</label>
            <input
              type="number"
              step="0.1"
              value={threshold}
              onChange={(e) => setThreshold(Number(e.target.value) || 1.5)}
            />
          </div>
          <button className="btn" onClick={() => void load()} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
      </div>

      {error && <div className="notice bad">{error}</div>}

      {progress && !progress.enabled && (
        <div className="notice warn">
          The observer is switched off (<code>POC_MEASUREMENT=false</code>). No new episodes are
          being recorded.
        </div>
      )}

      {progress && (
        <div className="grid cols-4">
          <div className="card stat">
            <div className="label">Episodes recorded</div>
            <div className="value">{progress.progress.total}</div>
            <div className="hint">{progress.progress.active} still being tracked</div>
          </div>
          <div className="card stat">
            <div className="label">Qualifying</div>
            <div className="value">{progress.progress.qualifying}</div>
            <div className="hint">≥ {progress.startThresholdPct}% net at detection</div>
          </div>
          <div className="card stat">
            <div className="label">Collection window</div>
            <div className="value">
              {progress.progress.firstAt && progress.progress.lastAt
                ? `${(
                    (new Date(progress.progress.lastAt).getTime() -
                      new Date(progress.progress.firstAt).getTime()) /
                    3_600_000
                  ).toFixed(1)}h`
                : '—'}
            </div>
            <div className="hint">Target: 2–3 days of continuous data</div>
          </div>
          <div className="card stat">
            <div className="label">Horizons</div>
            <div className="value" style={{ fontSize: 16 }}>
              {progress.horizonsMinutes.map((m) => `T+${m}`).join(' · ')}
            </div>
            <div className="hint">minutes after detection</div>
          </div>
        </div>
      )}

      {report && (
        <>
          <div className={`notice ${verdictClass}`}>
            <strong>
              {report.verdict.decision === 'go'
                ? 'GO'
                : report.verdict.decision === 'no-go'
                  ? 'NO-GO'
                  : 'INCONCLUSIVE'}
              {' — '}
              {report.verdict.headline}
            </strong>
            <ul style={{ margin: '10px 0 0', paddingLeft: 18, lineHeight: 1.6 }}>
              {report.verdict.reasoning.map((line) => (
                <li key={line}>{line}</li>
              ))}
            </ul>
          </div>

          <div className="card">
            <div className="card-head">
              <h2>Decay curve</h2>
              <span className="faint" style={{ fontSize: 12 }}>
                Buy price locked at T0 · sell price re-read at each horizon
              </span>
            </div>
            {report.horizons.length === 0 ? (
              <div className="empty">
                <strong>No horizon samples yet.</strong>
                Episodes need to reach their first horizon before a curve exists.
              </div>
            ) : (
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Horizon</th>
                      <th className="num">Measured</th>
                      <th className="num">Missing</th>
                      <th className="num">Still ≥{threshold}%</th>
                      <th className="num">Still ≥0%</th>
                      <th className="num">Median net</th>
                      <th className="num">Mean net</th>
                      <th className="num">P25</th>
                      <th className="num">P75</th>
                      <th className="num">Exec size</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.horizons.map((h) => (
                      <tr key={h.horizonMinutes}>
                        <td className="sym">T+{h.horizonMinutes}m</td>
                        <td className="num">{h.measured}</td>
                        <td className={`num ${h.missing > 0 ? 'amber' : 'faint'}`}>{h.missing}</td>
                        <td className={`num ${rateClass(h.survivalRate)}`}>
                          {(h.survivalRate * 100).toFixed(0)}% ({h.stillProfitable})
                        </td>
                        <td className={`num ${rateClass(h.breakevenRate)}`}>
                          {(h.breakevenRate * 100).toFixed(0)}% ({h.stillBreakeven})
                        </td>
                        <td className={`num ${netClass(h.medianNetPct)}`}>
                          {pct(h.medianNetPct, 2)}
                        </td>
                        <td className={`num ${netClass(h.meanNetPct)}`}>{pct(h.meanNetPct, 2)}</td>
                        <td className="num faint">{pct(h.p25NetPct, 2)}</td>
                        <td className="num faint">{pct(h.p75NetPct, 2)}</td>
                        <td className="num">
                          {h.medianExecutableQuote === null
                            ? '—'
                            : h.medianExecutableQuote.toFixed(0)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            <div className="card-body faint" style={{ fontSize: 12, borderTop: '1px solid var(--border)' }}>
              <strong>Missing</strong> means no fresh sell-side price existed at that horizon. Those
              are excluded from the ratios rather than counted as survival or as failure — inventing
              a price would invent an answer.
            </div>
          </div>

          {report.byCoin.length > 0 && (
            <div className="card">
              <div className="card-head">
                <h2>By coin</h2>
                <span className="faint" style={{ fontSize: 12 }}>
                  Expect heavy concentration in a few thin names
                </span>
              </div>
              <div className="table-scroll">
                <table>
                  <thead>
                    <tr>
                      <th>Coin</th>
                      <th className="num">Episodes</th>
                      <th className="num">Qualifying</th>
                      <th className="num">Median net T0</th>
                      <th className="num">Best net T0</th>
                      <th className="num">Survived</th>
                      <th className="num">Withdrawal fee</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.byCoin.slice(0, 25).map((c) => (
                      <tr key={c.coin}>
                        <td className="sym">{c.coin}</td>
                        <td className="num">{c.episodes}</td>
                        <td className="num">{c.qualifyingEpisodes}</td>
                        <td className={`num ${netClass(c.medianNetPctT0)}`}>
                          {pct(c.medianNetPctT0, 2)}
                        </td>
                        <td className={`num ${netClass(c.bestNetPctT0)}`}>
                          {pct(c.bestNetPctT0, 2)}
                        </td>
                        <td className="num">
                          {c.finalSurvivalRate === null
                            ? '—'
                            : `${(c.finalSurvivalRate * 100).toFixed(0)}%`}
                        </td>
                        <td className="num faint">
                          {c.withdrawalFeeCoin} {c.coin}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}

function rateClass(rate: number): string {
  if (rate >= 0.5) return 'green';
  if (rate >= 0.25) return 'amber';
  return 'red';
}

function netClass(value: number): string {
  if (value > 0) return 'green';
  if (value < 0) return 'red';
  return 'faint';
}
